import {
    useMutation,
    useQueryClient,
} from '@tanstack/react-query';
import {productCopy} from '../../../config/productCopy';
import {I18n} from 'aws-amplify/utils';

import * as DnsRecordsService from '../services/DnsRecordsService';
// ⚠️ THE CLASSIFIER IS IMPORTED, NOT COPIED. It parses the API's error
// envelope, which is identical for zones and records; a second copy would
// diverge the first time the contract adds a class. Only the COPY is
// record-specific — see ../writeErrorMessages.
import {classifyWriteError} from '../../zones/services/writeErrors';
import {dnsRecordsKeys} from './useDnsRecordsQuery';
import {recordWriteErrorCopy} from '../writeErrorMessages';
import {
    ApplyDnsRecordChangesInput,
    DnsRecord,
    DnsRecordChangeInput,
    DnsWriteResult,
    writeStateCopy,
} from '../types';
import {
    showErrorNotification,
    showSuccessNotification,
} from '../../../components/ServiceNotifications';

// FNP-1192 — THE RECORD WRITE HOOKS.
//
// ⚠️ ONE MUTATION SERVES CREATE, EDIT AND DELETE. `applyDnsRecordChanges` takes
// a batch, each change carrying UPSERT or DELETE. There is no bare CREATE
// because providers disagree about whether creating an existing record is an
// error, and idempotent writes are the only kind this platform wants.

const notifyWriteError = (e: unknown) => {
    const error = classifyWriteError(e);
    const copy = recordWriteErrorCopy(error.kind);
    // ⚠️ The provider's sentence is APPENDED, never substituted for. A
    // re-worded provider message is what made FNP-1087 take a whole
    // investigation.
    const detail = error.providerMessage
        ? `${copy.action} — ${error.providerMessage}`
        : copy.action;
    showErrorNotification(`${copy.title}. ${detail}`);
};

// ⚠️ THE SUCCESS NOTIFICATION REPORTS THE WRITE STATE, AND NEVER SAYS "DONE".
//
// A successful mutation is NOT a live record. The schema's three states each
// get their own sentence:
//
//   PENDING      — accepted, not yet propagated. Explicitly not live.
//   SETTLED      — live across the PROVIDER'S OWN nameservers, with the TTL
//                  caveat attached. Never the bare word "live".
//   NOT_REPORTED — the provider cannot say. Explicitly not a success claim.
//
// A result that came back null (the schema allows it) is the fourth case and it
// asserts nothing at all: the write may have landed, and inventing a state for
// it would be the exact fabrication the nullable return exists to avoid.
const notifyWriteResult = (result: DnsWriteResult | undefined, verb: string) => {

    if (!result) {
        showSuccessNotification(
            `${verb}. ${I18n.get('The provider did not report how far the change has got. Reload the records to see the current state.')}`,
        );
        return;
    }

    const copy = writeStateCopy(result.writeState);

    showSuccessNotification(`${verb}. ${productCopy(copy.title)} — ${productCopy(copy.detail)}`);
};

/**
 * Create or replace a record.
 *
 * ⚠️ AN EDIT THAT CHANGES THE NAME OR THE TYPE IS NOT ONE CHANGE.
 *
 * A record's identity IS (name, type). An UPSERT at a new pair creates a new
 * record and leaves the old one serving — so a "rename" done as a single UPSERT
 * silently doubles the record instead of moving it. The caller passes the
 * original pair when editing, and this assembles the DELETE of the old
 * alongside the UPSERT of the new, in ONE batch so the provider applies both or
 * neither.
 */
export const useUpsertRecordMutation = (connectionId?: string, zoneId?: string) => {

    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (args: {
            record: DnsRecordChangeInput,
            // The (name, type) this edit started from, when it is an edit.
            // Absent for a create.
            replacing?: Pick<DnsRecord, 'name' | 'type'>,
        }): Promise<DnsWriteResult | undefined> => {

            if (!connectionId || !zoneId) {
                throw new Error('connectionId and zoneId are required parameters');
            }

            const changes: DnsRecordChangeInput[] = [];

            // ⚠️ THE DELETE GOES FIRST IN THE BATCH. A provider applying the
            // batch in order removes the old pair before writing the new one,
            // which is the intent; the reverse order would briefly serve both.
            const renamed = args.replacing
                && (args.replacing.name !== args.record.name
                    || args.replacing.type !== args.record.type);

            if (renamed && args.replacing) {
                changes.push({
                    action: 'DELETE',
                    name: args.replacing.name,
                    type: args.replacing.type,
                    // The provider identifies the record to delete by name and
                    // type. TTL and values are required by the input type and
                    // are carried from the record being replaced.
                    ttlSeconds: args.record.ttlSeconds,
                    values: args.record.values,
                });
            }

            changes.push({
                ...args.record,
                action: 'UPSERT',
            });

            const input: ApplyDnsRecordChangesInput = {
                connectionId,
                zoneId,
                changes,
            };

            return DnsRecordsService.applyRecordChanges(input);
        },
        onSuccess: (result, args) => {
            notifyWriteResult(
                result,
                args.replacing ? I18n.get('Record saved') : I18n.get('Record created'),
            );
            // Every list for this zone, whatever filter it holds: a new NS
            // record must appear in the NS-filtered view as well as the
            // unfiltered one, and the filter is part of the key.
            queryClient.invalidateQueries({queryKey: dnsRecordsKeys.lists()});
        },
        onError: (e: unknown) => {
            notifyWriteError(e);
        },
    });
};

/**
 * Delete a record.
 *
 * ⚠️ IDENTIFIED BY (name, type), because that is all a record has. The TTL and
 * values are required by `DnsRecordChangeInput` and are carried from the record
 * being deleted rather than invented.
 */
export const useDeleteRecordMutation = (connectionId?: string, zoneId?: string) => {

    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (record: DnsRecord): Promise<DnsWriteResult | undefined> => {

            if (!connectionId || !zoneId) {
                throw new Error('connectionId and zoneId are required parameters');
            }

            const input: ApplyDnsRecordChangesInput = {
                connectionId,
                zoneId,
                changes: [{
                    action: 'DELETE',
                    name: record.name,
                    type: record.type,
                    // ⚠️ THE `?? 0` IS FOR A MISSING TTL, NOT FOR A POINTER.
                    //
                    // This comment used to claim the provider "matches on name
                    // and type", which is true for an ordinary record and
                    // FALSE for a pointer (FNP-1279): a provider matches a
                    // delete on the record's FULL definition, alias target
                    // included, so a pointer delete built from these fields is
                    // not the record that exists — it fails, or it removes a
                    // different record of the same name and type.
                    //
                    // A pointer therefore never reaches here: `isDeletableRecord`
                    // withholds the control, and the adapter refuses the write
                    // with a classified error if one ever does.
                    ttlSeconds: record.ttlSeconds ?? 0,
                    values: record.values ?? [],
                }],
            };

            return DnsRecordsService.applyRecordChanges(input);
        },
        onSuccess: (result) => {
            notifyWriteResult(result, I18n.get('Record deleted'));
            queryClient.invalidateQueries({queryKey: dnsRecordsKeys.lists()});
        },
        onError: (e: unknown) => {
            notifyWriteError(e);
        },
    });
};
